import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
  Divider,
  Stack,
  FormHelperText,
  Tabs,
  Tab,
  IconButton,
  InputAdornment,
  Checkbox,
  ListItemText,
  Chip,
  FormControlLabel,
  FormGroup,
  FormLabel,
} from '@mui/material';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import { useNavigate } from 'react-router-dom';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AddIcon from '@mui/icons-material/Add';
import AttachmentIcon from '@mui/icons-material/Attachment';
import DescriptionIcon from '@mui/icons-material/Description';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CampaignIcon from '@mui/icons-material/Campaign';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SendIcon from '@mui/icons-material/Send';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useAuth } from '../contexts/AuthContext';
import API_CONFIG from '../config/api';
import { apiFetch, getToken, openAuthenticatedUpload } from '../api/client';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import EditorTRModal from '../components/contratos/EditorTRModal';
import CamposTrDinamicosForm from '../components/contratos/CamposTrDinamicosForm';
import { CAMPOS_TR_PADRAO } from './contratos/utils/camposTrFluxo';
import EditorCISolicitacaoModal from '../components/contratos/EditorCISolicitacaoModal';
import AutorizadoresTrDialog from '../components/contratos/AutorizadoresTrDialog';
import EditorNotificacaoSolicitacaoDialog from '../components/contratos/EditorNotificacaoSolicitacaoDialog';
import ContratosModuloHeader from '../components/contratos/ContratosModuloHeader';
import SolicitacaoRowActions from '../components/contratos/SolicitacaoRowActions';
import SolicitacaoAditivoDialog from '../components/contratos/SolicitacaoAditivoDialog';
import VisaoRestritaBanner from '../components/VisaoRestritaBanner';
import { withSetorScope, isFiscalSetorial, getFiscalSetor } from '../utils/dataIsolation';
import {
  exportSolicitacoesExcel,
  exportSolicitacoesPdf,
} from './contratos/utils/solicitacoesExport';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableViewIcon from '@mui/icons-material/TableView';

const QUILL_WIZARD_MODULES = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['clean'],
  ],
};

const temTRAnexado = (item) => !!(item?.trPdfPath || item?.trDocxPath || item?.trFileUrl);
/** Pode avançar: TR anexado/URL OU texto gerado/preenchido no editor */
const temTRDisponivelParaAvancar = (item) =>
  temTRAnexado(item) || Boolean(item?.trPreenchido);

const TIPOS_SOLICITACAO = [
  { value: 'CI', label: 'CI (Comunicado Interno)' },
  { value: 'TR', label: 'Termo de Referência' },
  { value: 'NOTIFICACAO', label: 'Notificação' }
];

/** Tipos disponíveis no wizard (Nova Solicitação) */
const TIPOS_WIZARD = TIPOS_SOLICITACAO.filter((t) => t.value === 'CI' || t.value === 'NOTIFICACAO');

const SUBTIPOS_NOTIFICACAO = [
  { value: 'Fiscal', label: 'Fiscal' },
  { value: 'Prestador', label: 'Prestador' }
];

const TIPOS_CI = [
  { value: 'ADITIVO', label: 'Aditivo' },
  { value: 'CONTRATACAO', label: 'Contratação' }
];

const CI_TIPO_LABEL = {
  ADITIVO: 'Aditivo',
  CONTRATACAO: 'Contratação'
};

const DESTINOS_CI = [
  { value: 'GERENCIA_ADMINISTRATIVA', label: 'Diretoria Executiva' },
  { value: 'SETOR_CONTRATOS', label: 'Setor de Contratos' }
];

const DESTINOS_CI_LABEL = DESTINOS_CI.reduce((acc, item) => {
  acc[item.value] = item.label;
  return acc;
}, {});

async function baixarTrDocumentoComAuth(solicitacaoId, formato, setSnackbar) {
  try {
    const token = getToken();
    const res = await fetch(
      `${API_CONFIG.BACKEND_URL}/api/contratos/solicitacoes/${solicitacaoId}/tr/documento?formato=${formato}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} }
    );
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      if (setSnackbar) {
        setSnackbar({
          open: true,
          message: data.error || 'Erro ao baixar TR. Faça login novamente.',
          severity: 'error'
        });
      } else {
        alert(data.error || 'Erro ao baixar TR. Faça login novamente.');
      }
      return;
    }
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  } catch (e) {
    if (setSnackbar) {
      setSnackbar({
        open: true,
        message: 'Erro ao baixar TR.',
        severity: 'error'
      });
    } else {
      alert('Erro ao baixar TR.');
    }
  }
}

const TAB_CATEGORIAS = [
  {
    valor: 'CI',
    label: 'CI (Comunicado Interno)',
    descricao: 'Solicitações internas formalizadas via comunicado.',
    icone: <DescriptionIcon sx={{ fontSize: 20 }} />
  },
  {
    valor: 'TR',
    label: 'Termo de Referência',
    descricao: 'Histórico de TRs avulsos. Novos TRs são anexados na CI de Contratação.',
    icone: <AssignmentTurnedInIcon sx={{ fontSize: 20 }} />
  },
  {
    valor: 'NOTIFICACAO',
    label: 'Notificação',
    descricao: 'Comunicados direcionados a fiscais ou prestadores.',
    icone: <CampaignIcon sx={{ fontSize: 20 }} />
  }
];

function getColunasListagem(categoria) {
  const codigoTitulo =
    categoria === 'NOTIFICACAO' ? 'Nº Notificação' : categoria === 'TR' ? 'Nº TR' : 'Nº CI';
  return [
    { chave: 'acoes', titulo: 'Ações', largura: '220px' },
    { chave: 'codigo', titulo: codigoTitulo, largura: '140px' },
    { chave: 'titulo', titulo: 'Assunto', flex: 1 },
    { chave: 'unidade', titulo: 'Unidade', largura: '110px' },
    { chave: 'status', titulo: 'Status', largura: '120px' },
    { chave: 'destinatario', titulo: 'Destinatário(s)', largura: '200px' },
    { chave: 'contrato', titulo: 'Contrato', largura: '180px' },
    { chave: 'criadoEm', titulo: 'Criado em', largura: '160px' },
  ];
}

const STATUS_LABELS = {
  RASCUNHO: 'Rascunho',
  EM_ANALISE: 'Em análise',
  EM_FLUXO: 'Em fluxo',
  APROVADA: 'Aprovada',
  NEGADA: 'Negada',
  REPROVADA: 'Reprovada',
  EM_EXECUCAO: 'Em execução',
  CONCLUIDA: 'Concluída',
  CANCELADA: 'Cancelada'
};

function getFluxoEtapas(solicitacao) {
  if (!solicitacao) return ['Rascunho', 'Documentação', 'Processo', 'Concluída'];
  if (solicitacao.tipo === 'NOTIFICACAO') {
    return ['Rascunho', 'Texto / PDF', 'Envio registrado', 'Concluída'];
  }
  if (solicitacao.tipo === 'CI' && solicitacao.ciTipo === 'ADITIVO') {
    return ['Rascunho', 'Dados do aditivo', 'Processo', 'Concluída'];
  }
  if (solicitacao.tipo === 'TR') {
    return ['Rascunho', 'Documento TR', 'Vinculado', 'Concluída'];
  }
  return ['Rascunho', 'TR / Documentação', 'Processo de contratação', 'Concluída'];
}

const calcularEtapaFluxo = (solicitacao) => {
  if (!solicitacao) return 0;
  const status = (solicitacao.status || 'RASCUNHO').toUpperCase();
  if (['CONCLUIDA', 'APROVADA'].includes(status)) return 3;
  if (solicitacao.tipo === 'NOTIFICACAO') {
    if (solicitacao.notificacaoEnviada || status === 'EM_FLUXO') return 2;
    if (solicitacao.notificacaoPdfPath || solicitacao.arquivoUrl) return 1;
    return 0;
  }
  if (solicitacao.processo?.id) return 2;
  if (status === 'EM_ANALISE' || status === 'EM_FLUXO') return 2;
  if (solicitacao.trPdfPath || solicitacao.trDocxPath || solicitacao.trPreenchido) return 1;
  if (solicitacao.tipo === 'CI' && solicitacao.ciTipo === 'ADITIVO' && solicitacao.aditivoPreenchido) return 1;
  return 0;
};

function nomesDestinatarios(item) {
  const lista = [];
  if (Array.isArray(item?.destinatarios) && item.destinatarios.length) {
    item.destinatarios.forEach((d) => {
      if (d?.nome) lista.push(d.nome);
    });
  } else if (item?.destinatario?.nome) {
    lista.push(item.destinatario.nome);
  }
  return lista;
}

const formatDateTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return `${date.toLocaleDateString('pt-BR')} às ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const parts = dateString.split('-');
  if (parts.length !== 3) return dateString;
  const [year, month, day] = parts;
  if (!year || !month || !day) return dateString;
  return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
};

export default function ContratosSolicitacoesPage() {
  const navigate = useNavigate();
  const { unidadeSelecionada, user, rbacRole, fiscalSetor } = useAuth();
  const usuarioEmail = user?.email || '';
  const usuarioTipo = user?.tipo || '';
  const usuarioSetor = user?.setor || '';
  const scopeOpts = { user, rbacRole, fiscalSetor };
  /** TR é obrigação do solicitante/fiscal em toda CI de Contratação (não só perfil edital). */
  const exigeTRParaContratacao = true;
  /** Quem cria a solicitação pode (e deve) preencher o TR. Contratos só padroniza o template. */
  const podeGerirTR = true;
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [ciFile, setCiFile] = useState(null);
  const [trFile, setTrFile] = useState(null);
  const ciFileInputRef = useRef(null);
  const trFileInputRef = useRef(null);
  const [usuarios, setUsuarios] = useState([]);
  const [contratos, setContratos] = useState([]);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  const [loadingContratos, setLoadingContratos] = useState(false);
  const [categoriaAtiva, setCategoriaAtiva] = useState('CI');
  const [searchTerm, setSearchTerm] = useState('');
  const [detalhesSolicitacao, setDetalhesSolicitacao] = useState({ open: false, dados: null });
  const [excluirSolicitacao, setExcluirSolicitacao] = useState({ open: false, dados: null, loading: false });
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [periodoFrom, setPeriodoFrom] = useState('');
  const [periodoTo, setPeriodoTo] = useState('');
  const [dialogEditarTROpen, setDialogEditarTROpen] = useState(false);
  const [solicitacaoTRCtx, setSolicitacaoTRCtx] = useState({ id: null, titulo: '' });
  const [dialogEditarCIOpen, setDialogEditarCIOpen] = useState(false);
  const [solicitacaoCICtx, setSolicitacaoCICtx] = useState({ id: null, titulo: '' });
  const [dialogAutorizadores, setDialogAutorizadores] = useState({ open: false, item: null });
  const [enviandoProcesso, setEnviandoProcesso] = useState(false);
  const [drawerAditivo, setDrawerAditivo] = useState({ open: false, item: null });
  const [drawerNotificacao, setDrawerNotificacao] = useState({ open: false, item: null });
  const [exporting, setExporting] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [wizardErrors, setWizardErrors] = useState({});
  const [wizardDraftId, setWizardDraftId] = useState(null);
  const [wizardDraftCodigo, setWizardDraftCodigo] = useState('');
  const [trWizardOk, setTrWizardOk] = useState(false);
  const [criandoRascunho, setCriandoRascunho] = useState(false);
  const [camposTrSchema, setCamposTrSchema] = useState(CAMPOS_TR_PADRAO);
  const [camposTrValores, setCamposTrValores] = useState({});
  const [salvandoCamposTr, setSalvandoCamposTr] = useState(false);
  const [form, setForm] = useState({
    tipo: 'CI',
    subtipo: '',
    titulo: '',
    descricao: '',
    ciCorpoHtml: '',
    destinatarioIds: [],
    autorizadorIds: [],
    contratoId: '',
    dataReferencia: '',
    dataPrazo: '',
    ciTipo: '',
    ciDestinos: []
  });

  const isFluxoContratacao = form.tipo === 'CI' && form.ciTipo === 'CONTRATACAO';
  const WIZARD_STEPS = isFluxoContratacao
    ? ['Tipo', 'CI (fiscal)', 'TR (fiscal)', 'Autorizadores', 'Abrir processo']
    : ['Tipo de solicitação', 'Formulário e anexos', 'Revisão e envio'];

  const carregarSolicitacoes = useCallback(async () => {
    if (!usuarioEmail && !usuarioTipo && !usuarioSetor) {
      setSolicitacoes([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (unidadeSelecionada?.codigo) {
        params.append('unidade', unidadeSelecionada.codigo);
      }
      if (usuarioEmail) params.append('usuarioEmail', usuarioEmail);
      if (usuarioTipo) params.append('usuarioTipo', usuarioTipo);
      if (usuarioSetor) params.append('usuarioSetor', usuarioSetor);
      if (filtroTipo) params.append('tipo', filtroTipo);
      if (filtroStatus) params.append('status', filtroStatus);
      if (periodoFrom) params.append('periodoFrom', periodoFrom);
      if (periodoTo) params.append('periodoTo', periodoTo);
      const setorScope = getFiscalSetor(user, fiscalSetor);
      if (isFiscalSetorial(user, rbacRole) && setorScope) {
        params.append('setor', setorScope);
      }
      const path = withSetorScope(
        `/api/contratos/solicitacoes${params.toString() ? `?${params.toString()}` : ''}`,
        scopeOpts
      );
      const res = await apiFetch(path);
      if (!res.ok) {
        throw new Error('Erro ao buscar solicitações');
      }
      const data = await res.json();
      setSolicitacoes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      setSolicitacoes([]);
      setSnackbar({ open: true, message: error.message || 'Erro ao carregar solicitações', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [unidadeSelecionada?.codigo, usuarioEmail, usuarioTipo, usuarioSetor, filtroTipo, filtroStatus, periodoFrom, periodoTo, user, rbacRole, fiscalSetor]);

  const carregarUsuarios = useCallback(async () => {
    setLoadingUsuarios(true);
    try {
      const params = new URLSearchParams();
      // Unidade tenant: API filtra por código (ex.: CEAP-SOL)
      const unidadeCodigo = unidadeSelecionada?.codigo || unidadeSelecionada?.id;
      if (unidadeCodigo) {
        params.append('unidade', unidadeCodigo);
      }
      const res = await apiFetch(
        `/api/usuarios${params.toString() ? `?${params.toString()}` : ''}`
      );
      if (!res.ok) {
        throw new Error('Erro ao carregar destinatários');
      }
      const data = await res.json();
      setUsuarios(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      setUsuarios([]);
      setSnackbar({ open: true, message: error.message || 'Erro ao carregar destinatários', severity: 'error' });
    } finally {
      setLoadingUsuarios(false);
    }
  }, [unidadeSelecionada?.codigo, unidadeSelecionada?.id]);

  const carregarContratos = useCallback(async () => {
    setLoadingContratos(true);
    try {
      const params = new URLSearchParams();
      if (unidadeSelecionada?.codigo) {
        params.append('unidade', unidadeSelecionada.codigo);
      }
      const path = withSetorScope(
        `/api/prestadores${params.toString() ? `?${params.toString()}` : ''}`,
        scopeOpts
      );
      const res = await apiFetch(path);
      if (!res.ok) {
        throw new Error('Erro ao carregar contratos');
      }
      const data = await res.json();
      const contratosFormatados = Array.isArray(data)
        ? data
            .filter((item) => item && (item.numeroContrato || item.empresa))
            .map((item) => ({
              id: item.id,
              numero: item.numeroContrato,
              empresa: item.empresa,
              unidade: item.unidade
            }))
        : [];
      setContratos(contratosFormatados);
    } catch (error) {
      console.error(error);
      setContratos([]);
      setSnackbar({ open: true, message: error.message || 'Erro ao carregar contratos', severity: 'error' });
    } finally {
      setLoadingContratos(false);
    }
  }, [unidadeSelecionada?.codigo, user, rbacRole, fiscalSetor]);

  useEffect(() => {
    if (!usuarioEmail && !usuarioTipo && !usuarioSetor) return;
    carregarSolicitacoes();
  }, [carregarSolicitacoes, usuarioEmail, usuarioTipo, usuarioSetor]);

  useEffect(() => {
    const handleRefresh = (event) => {
      if (event?.detail?.source === 'solicitacoes') return;
      carregarSolicitacoes();
    };
    window.addEventListener('contratos:refresh', handleRefresh);
    return () => window.removeEventListener('contratos:refresh', handleRefresh);
  }, [carregarSolicitacoes]);

  useEffect(() => {
    setUsuarios([]);
    setContratos([]);
  }, [unidadeSelecionada?.codigo]);

  useEffect(() => {
    if (!dialogOpen) return;
    carregarUsuarios();
    carregarContratos();
  }, [dialogOpen, carregarUsuarios, carregarContratos]);

  const handleOpenDialog = () => {
    setWizardStep(0);
    setWizardErrors({});
    setDialogOpen(true);
    setForm((prev) => ({
      ...prev,
      tipo: categoriaAtiva === 'NOTIFICACAO' ? 'NOTIFICACAO' : 'CI'
    }));
  };

  const resetForm = () => {
    setForm({
      tipo: 'CI',
      subtipo: '',
      titulo: '',
      descricao: '',
      ciCorpoHtml: '',
      destinatarioIds: [],
      autorizadorIds: [],
      contratoId: '',
      dataReferencia: '',
      dataPrazo: '',
      ciTipo: '',
      ciDestinos: []
    });
    setWizardErrors({});
    setWizardDraftId(null);
    setWizardDraftCodigo('');
    setTrWizardOk(false);
    setCiFile(null);
    setTrFile(null);
    if (ciFileInputRef.current) ciFileInputRef.current.value = '';
    if (trFileInputRef.current) trFileInputRef.current.value = '';
  };

  const handleCloseDialog = () => {
    if (!saving) {
      setDialogOpen(false);
      setWizardStep(0);
      resetForm();
    }
  };

  const abrirDetalhesSolicitacao = (solicitacao) => {
    setDetalhesSolicitacao({ open: true, dados: solicitacao });
  };

  const fecharDetalhesSolicitacao = () => {
    setDetalhesSolicitacao({ open: false, dados: null });
  };

  const solicitarExclusaoSolicitacao = (solicitacao) => {
    setExcluirSolicitacao({ open: true, dados: solicitacao, loading: false });
  };

  const cancelarExclusaoSolicitacao = () => {
    setExcluirSolicitacao({ open: false, dados: null, loading: false });
  };

  const abrirEditarTR = (item) => {
    setSolicitacaoTRCtx({ id: item.id, titulo: item.titulo || '' });
    setDialogEditarTROpen(true);
  };

  const abrirEditarCI = (item) => {
    setSolicitacaoCICtx({ id: item.id, titulo: item.titulo || '' });
    setDialogEditarCIOpen(true);
  };

  const fecharEditarCI = () => {
    setDialogEditarCIOpen(false);
    setSolicitacaoCICtx({ id: null, titulo: '' });
    carregarSolicitacoes();
  };

  const enviarParaProcessoContratacao = async (item) => {
    if (!item?.id) return;
    // Fiscal/solicitante preenche CI + TR antes. Enviar Análise NÃO abre o editor do TR.
    if (exigeTRParaContratacao && item.ciTipo === 'CONTRATACAO' && !temTRDisponivelParaAvancar(item)) {
      setSnackbar({
        open: true,
        message:
          'Preencha o TR antes de enviar. O fiscal/solicitante cria a CI e o TR; em seguida abre o processo.',
        severity: 'warning',
      });
      return;
    }
    setDialogAutorizadores({ open: true, item });
  };

  const confirmarEnvioComAutorizadores = async (autorizadores, item) => {
    const alvo = item || dialogAutorizadores.item;
    if (!alvo?.id) return;
    setEnviandoProcesso(true);
    try {
      await apiFetch(`/api/contratos/solicitacoes/${alvo.id}/autorizadores`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ autorizadores }),
      });
      const res = await apiFetch(
        `/api/contratos/solicitacoes/${alvo.id}/enviar-processo-contratacao`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ autorizadores }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao enviar');
      setDialogAutorizadores({ open: false, item: null });
      const extra =
        data.assinaturaDocumentoId
          ? ' Atividades e Assinatura Digital do TR criadas.'
          : ' Atividades de autorização criadas.';
      setSnackbar({
        open: true,
        message: `Enviado para Análise no Processo.${extra}`,
        severity: 'success',
      });
      carregarSolicitacoes();
      navigate('/contratos/processo-contratacao');
    } catch (err) {
      throw err;
    } finally {
      setEnviandoProcesso(false);
    }
  };

  const abrirAditivo = (item) => {
    setDrawerAditivo({ open: true, item });
  };

  const abrirNotificacao = (item) => {
    setDrawerNotificacao({ open: true, item });
  };

  const confirmarExclusaoSolicitacao = async () => {
    const solicitacaoId = excluirSolicitacao?.dados?.id;
    if (!solicitacaoId) return;
    setExcluirSolicitacao((prev) => ({ ...prev, loading: true }));
    const deveFecharDetalhes = detalhesSolicitacao.open && detalhesSolicitacao.dados?.id === solicitacaoId;
    try {
      const response = await apiFetch(`/api/contratos/solicitacoes/${solicitacaoId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.error || 'Erro ao excluir solicitação');
      }
      setSolicitacoes((prev) => prev.filter((item) => item.id !== solicitacaoId));
      setSnackbar({ open: true, message: 'Solicitação removida com sucesso', severity: 'success' });
      cancelarExclusaoSolicitacao();
      if (deveFecharDetalhes) {
        fecharDetalhesSolicitacao();
      }
      carregarSolicitacoes();
      window.dispatchEvent(
        new CustomEvent('contratos:refresh', {
          detail: {
            source: 'solicitacoes',
            tipo: 'delete',
            solicitacaoId
          }
        })
      );
    } catch (error) {
      console.error('Erro ao excluir solicitação:', error);
      setSnackbar({ open: true, message: error.message || 'Erro ao excluir solicitação', severity: 'error' });
      setExcluirSolicitacao((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleTipoChange = (event) => {
    const novoTipo = event.target.value;
    setForm((prev) => {
      const isCI = novoTipo === 'CI';
      return {
        ...prev,
        tipo: novoTipo,
        subtipo: novoTipo === 'NOTIFICACAO' ? prev.subtipo : '',
        ciTipo: isCI ? prev.ciTipo : '',
        contratoId: isCI && prev.ciTipo === 'ADITIVO' ? prev.contratoId : '',
        destinatarioIds: prev.destinatarioIds || []
      };
    });
    if (novoTipo !== 'CI' || form.ciTipo !== 'CONTRATACAO') {
      setTrFile(null);
      if (trFileInputRef.current) trFileInputRef.current.value = '';
    }
  };

  const handleCiTipoChange = (event) => {
    const value = event.target.value;
    setForm((prev) => ({
      ...prev,
      ciTipo: value,
      contratoId: value === 'ADITIVO' ? prev.contratoId : '',
      autorizadorIds: value === 'CONTRATACAO' ? prev.autorizadorIds : [],
    }));
    setWizardDraftId(null);
    setWizardDraftCodigo('');
    setTrWizardOk(false);
    if (wizardStep > 0) setWizardStep(0);
    if (value !== 'CONTRATACAO') {
      setTrFile(null);
      if (trFileInputRef.current) trFileInputRef.current.value = '';
    }
  };

  const handleDestinatariosChange = (event) => {
    const value = event.target.value;
    const valores = typeof value === 'string' ? value.split(',') : value;
    setForm((prev) => ({
      ...prev,
      destinatarioIds: valores.map((id) => (typeof id === 'number' ? id : Number(id))).filter((id) => Number.isFinite(id))
    }));
  };

  const handleCiFileSelect = (event) => {
    const file = event.target.files?.[0] || null;
    setCiFile(file);
  };

  const corpoCiTemConteudo = (html) => {
    const v = String(html || '')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/gi, ' ')
      .trim();
    return v.length > 0;
  };

  const verificarTrDaSolicitacao = async (solicitacaoId) => {
    if (!solicitacaoId) return false;
    try {
      const res = await apiFetch(`/api/contratos/solicitacoes/${solicitacaoId}/tr`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return false;
      return Boolean(data.clausulasPreenchidas || data.camposPreenchidos || data.temAnexoTrPdf);
    } catch {
      return false;
    }
  };

  const carregarCamposTrWizard = useCallback(async (solicitacaoId) => {
    if (!solicitacaoId) return;
    try {
      const res = await apiFetch(`/api/contratos/solicitacoes/${solicitacaoId}/tr`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return;
      if (Array.isArray(data.camposTrSchema) && data.camposTrSchema.length) {
        setCamposTrSchema(data.camposTrSchema);
      }
      if (data.campos && typeof data.campos === 'object') {
        setCamposTrValores(data.campos);
      }
      setTrWizardOk(Boolean(data.clausulasPreenchidas || data.camposPreenchidos || data.temAnexoTrPdf));
    } catch (e) {
      console.warn('carregarCamposTrWizard', e);
    }
  }, []);

  const salvarCamposTrWizard = async () => {
    if (!wizardDraftId) {
      setSnackbar({ open: true, message: 'Salve a CI primeiro (passo anterior).', severity: 'warning' });
      return;
    }
    setSalvandoCamposTr(true);
    try {
      const resGet = await apiFetch(`/api/contratos/solicitacoes/${wizardDraftId}/tr`);
      const dataGet = await resGet.json().catch(() => ({}));
      if (!resGet.ok) throw new Error(dataGet.error || 'Erro ao carregar TR');
      const res = await apiFetch(`/api/contratos/solicitacoes/${wizardDraftId}/tr`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clausulas: Array.isArray(dataGet.clausulas) ? dataGet.clausulas : [],
          campos: camposTrValores,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao salvar campos do TR');
      const ok = await verificarTrDaSolicitacao(wizardDraftId);
      setTrWizardOk(ok);
      setWizardErrors((err) => ({ ...err, tr: undefined }));
      setSnackbar({
        open: true,
        message: ok ? 'Campos do TR salvos.' : 'Campos salvos. Preencha o objeto ou as cláusulas.',
        severity: ok ? 'success' : 'warning',
      });
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Erro ao salvar campos', severity: 'error' });
    } finally {
      setSalvandoCamposTr(false);
    }
  };

  const criarRascunhoContratacao = async () => {
    if (wizardDraftId) return wizardDraftId;
    setCriandoRascunho(true);
    try {
      const formData = new FormData();
      formData.append('tipo', 'CI');
      formData.append('ciTipo', 'CONTRATACAO');
      formData.append('titulo', form.titulo.trim());
      formData.append('descricao', form.descricao ? form.descricao.trim() : '');
      formData.append(
        'ciJson',
        JSON.stringify({
          assunto: form.titulo.trim(),
          corpoHtml: form.ciCorpoHtml || '',
        })
      );
      if (form.dataReferencia) formData.append('dataReferencia', form.dataReferencia);
      if (unidadeSelecionada?.codigo) formData.append('unidade', unidadeSelecionada.codigo);
      if (user?.id) formData.append('solicitanteId', String(user.id));
      if (ciFile) {
        formData.append('ciFile', ciFile);
        formData.append('arquivo', ciFile);
      }

      const res = await apiFetch('/api/contratos/solicitacoes', {
        method: 'POST',
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Erro ao criar rascunho da CI');

      const id = data?.id || data?.solicitacao?.id;
      if (!id) throw new Error('Rascunho criado sem ID.');
      setWizardDraftId(id);
      setWizardDraftCodigo(data?.codigo || data?.solicitacao?.codigo || '');
      return id;
    } finally {
      setCriandoRascunho(false);
    }
  };

  const validarWizardStep = (step) => {
    const errors = {};
    if (isFluxoContratacao) {
      if (step === 0) {
        if (!form.ciTipo) errors.ciTipo = 'Selecione se a CI é Aditivo ou Contratação';
      }
      if (step === 1) {
        if (!form.titulo.trim()) errors.titulo = 'Informe o assunto/título da CI';
        if (!corpoCiTemConteudo(form.ciCorpoHtml)) {
          errors.ciCorpoHtml = 'Preencha o corpo da CI no sistema';
        }
      }
      if (step === 2) {
        if (!wizardDraftId) errors.tr = 'Salve a CI antes de editar o TR';
        else if (!trWizardOk) errors.tr = 'Abra o editor e salve o TR antes de continuar';
      }
      if (step === 3) {
        if (!form.autorizadorIds?.length) {
          errors.autorizadorIds = 'Selecione quem autoriza e assina o TR';
        }
      }
    } else {
      if (step === 0) {
        if (form.tipo === 'NOTIFICACAO' && !form.subtipo) {
          errors.subtipo = 'Selecione se a notificação é para Fiscal ou Prestador';
        }
        if (form.tipo === 'CI' && !form.ciTipo) {
          errors.ciTipo = 'Selecione se a CI é Aditivo ou Contratação';
        }
        if (form.tipo === 'CI' && (!form.ciDestinos || form.ciDestinos.length === 0)) {
          errors.ciDestinos = 'Selecione ao menos um destino (setor)';
        }
        if (!form.destinatarioIds || form.destinatarioIds.length === 0) {
          errors.destinatarioIds = 'Selecione ao menos um usuário em "Direcionado para"';
        }
      }
      if (step === 1) {
        if (!form.titulo.trim()) {
          errors.titulo = 'Informe o título da solicitação';
        }
        if (form.tipo === 'CI' && form.ciTipo === 'ADITIVO' && !form.contratoId) {
          errors.contratoId = 'Selecione o contrato que receberá o aditivo';
        }
      }
    }
    setWizardErrors(errors);
    if (Object.keys(errors).length) {
      const first = Object.values(errors)[0];
      setSnackbar({ open: true, message: first, severity: 'warning' });
      return false;
    }
    return true;
  };

  const avancarWizard = async () => {
    if (!validarWizardStep(wizardStep)) return;

    let draftIdAtual = wizardDraftId;

    if (isFluxoContratacao && wizardStep === 1) {
      try {
        draftIdAtual = await criarRascunhoContratacao();
        if (draftIdAtual) {
          await apiFetch(`/api/contratos/solicitacoes/${draftIdAtual}/ci`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              assunto: form.titulo.trim(),
              corpoHtml: form.ciCorpoHtml || '',
            }),
          });
        }
        const okTr = await verificarTrDaSolicitacao(draftIdAtual);
        setTrWizardOk(okTr);
      } catch (err) {
        setSnackbar({
          open: true,
          message: err.message || 'Erro ao salvar rascunho da CI',
          severity: 'error',
        });
        return;
      }
    }

    if (isFluxoContratacao && wizardStep === 2) {
      const okTr = await verificarTrDaSolicitacao(wizardDraftId || draftIdAtual);
      setTrWizardOk(okTr);
      if (!okTr) {
        setWizardErrors({ tr: 'Preencha e salve o TR (passo do fiscal) antes de continuar' });
        setSnackbar({
          open: true,
          message: 'Preencha e salve o TR antes de abrir o processo. Isso é etapa do fiscal/solicitante.',
          severity: 'warning',
        });
        // Reabre o editor para o fiscal completar
        const sid = wizardDraftId || draftIdAtual;
        if (sid) {
          setSolicitacaoTRCtx({ id: sid, titulo: form.titulo || '' });
          setDialogEditarTROpen(true);
        }
        return;
      }
    }

    const next = Math.min(wizardStep + 1, WIZARD_STEPS.length - 1);
    setWizardStep(next);

    // Saiu da CI → entra no TR: carrega campos dinâmicos (fiscal preenche inline)
    if (isFluxoContratacao && wizardStep === 1 && next === 2 && draftIdAtual) {
      carregarCamposTrWizard(draftIdAtual);
    }
  };

  const voltarWizard = () => setWizardStep((s) => Math.max(s - 1, 0));

  const tipoSolicitacaoLabel = (tipo) =>
    TIPOS_SOLICITACAO.find((t) => t.value === tipo)?.label || tipo;

  const abrirTrNoWizard = () => {
    if (!wizardDraftId) {
      setSnackbar({ open: true, message: 'Salve a CI primeiro (passo anterior).', severity: 'warning' });
      return;
    }
    setSolicitacaoTRCtx({ id: wizardDraftId, titulo: form.titulo || '' });
    setDialogEditarTROpen(true);
  };

  const fecharEditarTR = async () => {
    setDialogEditarTROpen(false);
    const sid = solicitacaoTRCtx.id;
    setSolicitacaoTRCtx({ id: null, titulo: '' });
    if (sid && (sid === wizardDraftId || isFluxoContratacao)) {
      const ok = await verificarTrDaSolicitacao(sid);
      setTrWizardOk(ok);
      await carregarCamposTrWizard(sid);
    }
    carregarSolicitacoes();
  };

  const handleSubmitContratacao = async () => {
    if (!validarWizardStep(3)) return;
    if (!wizardDraftId) {
      setSnackbar({ open: true, message: 'Rascunho da CI não encontrado.', severity: 'error' });
      return;
    }
    const okTr = await verificarTrDaSolicitacao(wizardDraftId);
    if (!okTr) {
      setSnackbar({ open: true, message: 'Preencha e salve o TR antes de enviar.', severity: 'warning' });
      setWizardStep(2);
      return;
    }

    const autorizadores = (form.autorizadorIds || [])
      .map((id) => {
        const u = usuarios.find((x) => String(x.id) === String(id));
        if (!u?.email) return null;
        return { id: u.id, nome: u.nome, email: u.email };
      })
      .filter(Boolean);

    if (!autorizadores.length) {
      setSnackbar({
        open: true,
        message: 'Selecione autorizadores com e-mail cadastrado.',
        severity: 'warning',
      });
      setWizardStep(3);
      return;
    }

    setSaving(true);
    try {
      await apiFetch(`/api/contratos/solicitacoes/${wizardDraftId}/ci`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          assunto: form.titulo.trim(),
          corpoHtml: form.ciCorpoHtml || '',
        }),
      });
      await apiFetch(`/api/contratos/solicitacoes/${wizardDraftId}/autorizadores`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ autorizadores }),
      });
      const res = await apiFetch(
        `/api/contratos/solicitacoes/${wizardDraftId}/enviar-processo-contratacao`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ autorizadores }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao enviar para processo');

      setSnackbar({
        open: true,
        message: 'CI enviada: processo aberto + atividades e Assinatura Digital do TR.',
        severity: 'success',
      });
      setDialogOpen(false);
      setWizardStep(0);
      resetForm();
      carregarSolicitacoes();
      navigate('/contratos/processo-contratacao');
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: error.message || 'Erro ao enviar solicitação',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (isFluxoContratacao) {
      await handleSubmitContratacao();
      return;
    }
    if (!validarWizardStep(0) || !validarWizardStep(1)) {
      return;
    }
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('tipo', form.tipo);
      if (form.tipo === 'CI' && form.ciTipo) {
        formData.append('ciTipo', form.ciTipo);
      }
      if (form.tipo === 'CI' && form.ciDestinos?.length) {
        formData.append('ciDestinos', JSON.stringify(form.ciDestinos));
      }
      if (form.tipo === 'NOTIFICACAO') {
        formData.append('subtipo', form.subtipo);
      }
      formData.append('titulo', form.titulo.trim());
      formData.append('descricao', form.descricao ? form.descricao.trim() : '');
      formData.append('destinatarioIds', JSON.stringify(form.destinatarioIds || []));
      if (form.destinatarioIds?.[0]) {
        formData.append('destinatarioId', String(form.destinatarioIds[0]));
      }
      if (form.contratoId) {
        formData.append('contratoId', form.contratoId);
      }
      if (form.dataReferencia) {
        formData.append('dataReferencia', form.dataReferencia);
      }
      if (form.tipo === 'NOTIFICACAO' && form.dataPrazo) {
        formData.append('dataPrazo', form.dataPrazo);
      }
      if (unidadeSelecionada?.codigo) {
        formData.append('unidade', unidadeSelecionada.codigo);
      }
      if (user?.id) {
        formData.append('solicitanteId', String(user.id));
      }
      if (ciFile) {
        formData.append('ciFile', ciFile);
        formData.append('arquivo', ciFile);
      }
      if (trFile) {
        formData.append('trFile', trFile);
      }

      const res = await apiFetch('/api/contratos/solicitacoes', {
        method: 'POST',
        body: formData
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || 'Erro ao salvar solicitação');
      }

      setSnackbar({ open: true, message: 'Solicitação cadastrada com sucesso!', severity: 'success' });
      setDialogOpen(false);
      setWizardStep(0);
      resetForm();
      carregarSolicitacoes();
      window.dispatchEvent(
        new CustomEvent('contratos:refresh', {
          detail: {
            source: 'solicitacoes',
            tipo: 'create',
            solicitacaoId: data?.id || null
          }
        })
      );
    } catch (error) {
      console.error(error);
      setSnackbar({ open: true, message: error.message || 'Erro ao salvar solicitação', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const groupedSolicitacoes = useMemo(() => {
    const groups = {
      CI: [],
      TR: [],
      NOTIFICACAO_FISCAL: [],
      NOTIFICACAO_PRESTADOR: []
    };

    const filtro = searchTerm.trim().toLowerCase();

    solicitacoes.forEach((item) => {
      const texto = `${item.codigo || ''} ${item.titulo || ''} ${item.descricao || ''}`.toLowerCase();
      if (filtro && !texto.includes(filtro)) {
        return;
      }
      if (item.tipo === 'CI') {
        groups.CI.push(item);
      } else if (item.tipo === 'TR') {
        groups.TR.push(item);
      } else if (item.tipo === 'NOTIFICACAO') {
        if ((item.subtipo || '').toLowerCase() === 'fiscal') {
          groups.NOTIFICACAO_FISCAL.push(item);
        } else {
          groups.NOTIFICACAO_PRESTADOR.push(item);
        }
      }
    });
    return groups;
  }, [solicitacoes, searchTerm]);

  const totaisCategoria = useMemo(
    () => ({
      CI: groupedSolicitacoes.CI.length,
      TR: groupedSolicitacoes.TR.length,
      NOTIFICACAO:
        groupedSolicitacoes.NOTIFICACAO_FISCAL.length + groupedSolicitacoes.NOTIFICACAO_PRESTADOR.length
    }),
    [groupedSolicitacoes]
  );

  const solicitacoesDaCategoria = useMemo(() => {
    if (categoriaAtiva === 'CI') return groupedSolicitacoes.CI;
    if (categoriaAtiva === 'TR') return groupedSolicitacoes.TR;
    return [...groupedSolicitacoes.NOTIFICACAO_FISCAL, ...groupedSolicitacoes.NOTIFICACAO_PRESTADOR];
  }, [categoriaAtiva, groupedSolicitacoes]);

  const categoriaSelecionada = useMemo(
    () => TAB_CATEGORIAS.find((tab) => tab.valor === categoriaAtiva),
    [categoriaAtiva]
  );

  const colunasListagem = useMemo(() => getColunasListagem(categoriaAtiva), [categoriaAtiva]);

  const gridTemplateColumns = useMemo(
    () =>
      colunasListagem
        .map((coluna) =>
          coluna.flex ? `minmax(220px, ${coluna.flex}fr)` : `minmax(${coluna.largura}, ${coluna.largura})`
        )
        .join(' '),
    [colunasListagem]
  );

  const textoRodape = useMemo(() => {
    const total = solicitacoesDaCategoria.length;
    if (total === 0) {
      return 'Mostrando 1 até 0 de 0 registros';
    }
    const plural = total > 1 ? 's' : '';
    return `Mostrando 1 até ${total} de ${total} registro${plural}`;
  }, [solicitacoesDaCategoria.length]);

  const solicitacaoSelecionada = detalhesSolicitacao.dados;
  const detalhesDestinos = useMemo(() => {
    if (!solicitacaoSelecionada) return [];
    if (solicitacaoSelecionada.ciDestinos?.length) {
      return solicitacaoSelecionada.ciDestinos.map(
        (destino) => DESTINOS_CI_LABEL[destino] || destino
      );
    }
    return [];
  }, [solicitacaoSelecionada]);

  return (
    <Box sx={{ p: 2 }}>
      <VisaoRestritaBanner />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <ContratosModuloHeader />
      </Box>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          mb: 3,
          borderRadius: 4,
          background: 'linear-gradient(120deg, rgba(227,242,253,0.85) 0%, rgba(255,255,255,0.95) 100%)',
          border: '1px solid rgba(21,101,192,0.18)',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2.5
        }}
      >
        <Tabs
          value={categoriaAtiva}
          onChange={(_, value) => setCategoriaAtiva(value)}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{
            minHeight: 68,
            '& .MuiTab-root': {
              textTransform: 'none',
              alignItems: 'flex-start',
              fontWeight: 700,
              padding: '12px 20px',
              borderRadius: 3,
              color: 'rgba(21,101,192,0.7)'
            },
            '& .Mui-selected': {
              color: '#0d47a1 !important',
              backgroundColor: 'rgba(21,101,192,0.12)'
            },
            '& .MuiTabs-indicator': {
              height: 0
            }
          }}
        >
          {TAB_CATEGORIAS.map((tab) => (
            <Tab
              key={tab.valor}
              value={tab.valor}
              disableRipple
              label={
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      backgroundColor: 'rgba(21,101,192,0.12)',
                      color: categoriaAtiva === tab.valor ? 'primary.main' : 'rgba(21,101,192,0.6)',
                      display: 'flex'
                    }}
                  >
                    {tab.icone}
                  </Box>
                  <Box textAlign="left">
                    <Typography variant="body1" fontWeight={700}>
                      {tab.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {totaisCategoria[tab.valor] || 0} registro(s)
                    </Typography>
                  </Box>
                </Box>
              }
            />
          ))}
        </Tabs>

        <Box display="flex" alignItems="center" gap={1.5} sx={{ ml: 'auto', flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<WorkHistoryIcon />}
            onClick={() => navigate('/contratos/processo-contratacao')}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Processos
          </Button>
          <Button
            variant="outlined"
            startIcon={<AssignmentIcon />}
            onClick={() => navigate('/contratos/atividades')}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Atividades
          </Button>
          <IconButton
            color="primary"
            onClick={carregarSolicitacoes}
            sx={{ backgroundColor: 'rgba(13,71,161,0.08)' }}
          >
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
            disabled={categoriaAtiva === 'TR'}
            sx={{
              textTransform: 'none',
              px: { xs: 2.5, md: 3 },
              py: 1.25,
              fontWeight: 700,
              borderRadius: 3,
              boxShadow: '0 14px 28px rgba(13,71,161,0.22)'
            }}
          >
            Nova Solicitação
          </Button>
        </Box>
      </Paper>

      {categoriaAtiva === 'TR' && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          Esta aba lista TRs avulsos históricos. Para novas contratações, use a aba CI e anexe/edite o TR na solicitação.
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid rgba(21,101,192,0.2)',
          overflow: 'hidden',
          background: 'rgba(255,255,255,0.92)',
          mb: 3
        }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            py: 2.5,
            background: 'linear-gradient(120deg, rgba(227,242,253,0.5) 0%, rgba(255,255,255,0.9) 100%)',
            borderBottom: '1px solid rgba(21,101,192,0.15)'
          }}
        >
          <Typography variant="subtitle1" fontWeight={800} color="primary">
            Lista de solicitações
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {categoriaSelecionada?.descricao || 'Selecione uma categoria para visualizar as solicitações registradas.'}
          </Typography>
        </Box>

        <Box
          sx={{
            px: { xs: 2, md: 3 },
            py: 2.5,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between'
          }}
        >
          <TextField
            size="small"
            placeholder='Pesquisar'
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
            sx={{
              minWidth: { xs: '100%', sm: 220 },
              backgroundColor: 'white',
              borderRadius: 2
            }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Tipo</InputLabel>
            <Select
              value={filtroTipo}
              label="Tipo"
              onChange={(e) => setFiltroTipo(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="CONTRATACAO">Contratação</MenuItem>
              <MenuItem value="ADITIVO">Aditivo</MenuItem>
              <MenuItem value="NOTIFICACAO">Notificação</MenuItem>
              <MenuItem value="TR">TR</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filtroStatus}
              label="Status"
              onChange={(e) => setFiltroStatus(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="De"
            type="date"
            value={periodoFrom}
            onChange={(e) => setPeriodoFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 140 }}
          />
          <TextField
            size="small"
            label="Até"
            type="date"
            value={periodoTo}
            onChange={(e) => setPeriodoTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 140 }}
          />
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PictureAsPdfIcon fontSize="small" />}
              disabled={exporting || loading || !solicitacoesDaCategoria.length}
              onClick={() => {
                try {
                  setExporting(true);
                  const n = exportSolicitacoesPdf(solicitacoesDaCategoria, {
                    categoriaLabel: categoriaSelecionada?.label || categoriaAtiva,
                    unidade: unidadeSelecionada?.codigo || '',
                  });
                  setSnackbar({
                    open: true,
                    message: `PDF gerado com ${n} registro(s).`,
                    severity: 'success',
                  });
                } catch (e) {
                  setSnackbar({
                    open: true,
                    message: e.message || 'Erro ao exportar PDF.',
                    severity: 'error',
                  });
                } finally {
                  setExporting(false);
                }
              }}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              PDF
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<TableViewIcon fontSize="small" />}
              disabled={exporting || loading || !solicitacoesDaCategoria.length}
              onClick={() => {
                try {
                  setExporting(true);
                  const n = exportSolicitacoesExcel(solicitacoesDaCategoria, {
                    categoriaLabel: categoriaSelecionada?.label || categoriaAtiva,
                  });
                  setSnackbar({
                    open: true,
                    message: `Excel (CSV) gerado com ${n} registro(s).`,
                    severity: 'success',
                  });
                } catch (e) {
                  setSnackbar({
                    open: true,
                    message: e.message || 'Erro ao exportar Excel.',
                    severity: 'error',
                  });
                } finally {
                  setExporting(false);
                }
              }}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Excel
            </Button>
          </Box>
        </Box>

        <Divider />

        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <Box sx={{ minWidth: 960 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns,
                px: { xs: 2, md: 3 },
                py: 1.5,
                backgroundColor: 'rgba(227,242,253,0.6)',
                color: 'primary.main',
                fontWeight: 700,
                fontSize: 13,
                textTransform: 'uppercase',
                letterSpacing: 0.4
              }}
            >
              {colunasListagem.map((coluna) => (
                <Box
                  key={coluna.chave}
                  sx={{
                    minWidth: coluna.flex ? undefined : coluna.largura,
                    fontWeight: 700
                  }}
                >
                  {coluna.titulo}
                </Box>
              ))}
            </Box>

            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={6}>
                <CircularProgress size={28} />
              </Box>
            ) : solicitacoesDaCategoria.length === 0 ? (
              <Box
                sx={{
                  px: { xs: 2, md: 3 },
                  py: 6,
                  textAlign: 'center',
                  color: 'text.secondary'
                }}
              >
                Nenhuma solicitação encontrada para esta categoria.
              </Box>
            ) : (
              solicitacoesDaCategoria.map((item, index) => {
                const contratoLabel = item.contrato
                  ? [item.contrato.numero, item.contrato.empresa].filter(Boolean).join(' · ')
                  : '—';
                const criadoEmData = item.createdAt ? formatDateTime(item.createdAt) : '—';
                const destNomes = nomesDestinatarios(item);
                return (
                  <Box
                    key={`${item.id}-${item.codigo}-${index}`}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns,
                      px: { xs: 2, md: 3 },
                      py: 1.75,
                      alignItems: 'center',
                      backgroundColor: index % 2 === 0 ? 'rgba(248,250,255,0.7)' : 'transparent',
                      borderBottom: '1px solid rgba(224, 242, 254, 0.6)'
                    }}
                  >
                    <Box>
                      <SolicitacaoRowActions
                        item={item}
                        podeGerirTR={podeGerirTR}
                        temTRDisponivel={temTRDisponivelParaAvancar(item)}
                        enviandoProcesso={enviandoProcesso}
                        onVer={abrirDetalhesSolicitacao}
                        onExcluir={solicitarExclusaoSolicitacao}
                        onAbrirCI={abrirEditarCI}
                        onAbrirTR={abrirEditarTR}
                        onEnviarProcesso={enviarParaProcessoContratacao}
                        onAbrirProcesso={() => navigate('/contratos/processo-contratacao')}
                        onAbrirAditivo={abrirAditivo}
                        onAbrirNotificacao={abrirNotificacao}
                        onBaixarTrPdf={(row) => baixarTrDocumentoComAuth(row.id, 'pdf', setSnackbar)}
                        onBaixarTrDocx={(row) => baixarTrDocumentoComAuth(row.id, 'docx', setSnackbar)}
                        onAbrirAnexo={(row) => openAuthenticatedUpload(row.arquivoUrl)}
                      />
                    </Box>
                    <Typography variant="body2" fontWeight={600} color="text.primary">
                      {item.codigo}
                    </Typography>
                    <Box>
                      <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
                        {item.titulo || '—'}
                      </Typography>
                      {item.tipo === 'CI' && item.ciTipo && (
                        <Typography variant="caption" color="text.secondary">
                          {CI_TIPO_LABEL[item.ciTipo] || item.ciTipo}
                        </Typography>
                      )}
                      {item.tipo === 'NOTIFICACAO' && item.subtipo && (
                        <Typography variant="caption" color="text.secondary">
                          {item.subtipo}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {item.unidade || '—'}
                    </Typography>
                    <Box>
                      {(() => {
                        const statusUpper = (item.status || 'RASCUNHO').toUpperCase();
                        const isEmFluxo = statusUpper === 'EM_FLUXO';
                        const isAprovada = statusUpper === 'APROVADA';
                        const isNegadaOuCancelada =
                          statusUpper === 'NEGADA' || statusUpper === 'CANCELADA';
                        const bgColor = isEmFluxo
                          ? 'rgba(25, 118, 210, 0.12)'
                          : isAprovada
                          ? 'rgba(46, 125, 50, 0.12)'
                          : isNegadaOuCancelada
                          ? 'rgba(211, 47, 47, 0.12)'
                          : 'rgba(158, 158, 158, 0.12)';
                        const textColor = isEmFluxo
                          ? '#1565c0'
                          : isAprovada
                          ? '#2e7d32'
                          : isNegadaOuCancelada
                          ? '#d32f2f'
                          : '#616161';
                        return (
                      <Chip
                        size="small"
                        label={STATUS_LABELS[item.status] || item.status || 'Rascunho'}
                        sx={{
                          bgcolor: bgColor,
                          color: textColor
                        }}
                      />
                        );
                      })()}
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {destNomes.length ? (
                        destNomes.map((nome) => (
                          <Chip key={nome} size="small" label={nome} variant="outlined" sx={{ maxWidth: 180 }} />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {contratoLabel || '—'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {criadoEmData}
                    </Typography>
                  </Box>
                );
              })
            )}
          </Box>
        </Box>

        <Divider />

        <Box
          sx={{
            px: { xs: 2, md: 3 },
            py: 2,
            backgroundColor: 'rgba(248,250,255,0.9)',
            textAlign: 'left'
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {textoRodape}
          </Typography>
        </Box>
      </Paper>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1.5 }}>
          Nova Solicitação
          <Typography variant="body2" color="text.secondary">
            Assistente passo a passo — cadastre comunicações e notificações.
          </Typography>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            background: 'linear-gradient(180deg, rgba(243,248,255,0.9) 0%, rgba(255,255,255,0.95) 100%)'
          }}
        >
          <Stepper activeStep={wizardStep} alternativeLabel sx={{ mb: 3 }}>
            {WIZARD_STEPS.map((label) => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>
          <Stack spacing={2.5}>
            {wizardStep === 0 && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: '1px solid rgba(25,118,210,0.15)',
                backgroundColor: 'rgba(255,255,255,0.9)'
              }}
            >
              <Typography variant="subtitle1" fontWeight={700} color="primary">
                Tipo de solicitação
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Escolha CI ou Notificação, o subtipo correspondente e para quem direcionar.
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1.5 }}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tipo de Solicitação</InputLabel>
                    <Select
                      value={form.tipo}
                      label="Tipo de Solicitação"
                      onChange={handleTipoChange}
                    >
                      {TIPOS_WIZARD.map((tipo) => (
                        <MenuItem key={tipo.value} value={tipo.value}>
                          {tipo.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {form.tipo === 'NOTIFICACAO' && (
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small" required error={!!wizardErrors.subtipo}>
                      <InputLabel>Notificação para</InputLabel>
                      <Select
                        value={form.subtipo}
                        label="Notificação para"
                        onChange={(event) => {
                          setForm((prev) => ({ ...prev, subtipo: event.target.value }));
                          setWizardErrors((e) => ({ ...e, subtipo: undefined }));
                        }}
                      >
                        {SUBTIPOS_NOTIFICACAO.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {wizardErrors.subtipo && <FormHelperText>{wizardErrors.subtipo}</FormHelperText>}
                    </FormControl>
                  </Grid>
                )}
                {form.tipo === 'CI' && (
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small" required error={!!wizardErrors.ciTipo}>
                      <InputLabel>Tipo de CI</InputLabel>
                      <Select
                        value={form.ciTipo}
                        label="Tipo de CI"
                        onChange={(e) => {
                          handleCiTipoChange(e);
                          setWizardErrors((err) => ({ ...err, ciTipo: undefined }));
                        }}
                      >
                        {TIPOS_CI.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{wizardErrors.ciTipo || 'Escolha entre aditivo ou contratação.'}</FormHelperText>
                    </FormControl>
                  </Grid>
                )}
                {form.tipo === 'CI' && form.ciTipo === 'CONTRATACAO' && (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      <strong>Quem faz o quê:</strong> o fiscal/solicitante preenche a{' '}
                      <strong>CI</strong> e o <strong>TR</strong>. O Setor de Contratos só padroniza o
                      template (cláusulas fixas). Depois disso, abre o processo de contratação.
                    </Alert>
                  </Grid>
                )}
                {form.tipo === 'CI' && form.ciTipo === 'ADITIVO' && (
                  <Grid item xs={12}>
                    <FormControl component="fieldset" error={!!wizardErrors.ciDestinos} variant="standard">
                      <FormLabel component="legend" sx={{ fontSize: 13, mb: 0.5 }}>
                        Destinos (setor)
                      </FormLabel>
                      <FormGroup row>
                        {DESTINOS_CI.map((destino) => (
                          <FormControlLabel
                            key={destino.value}
                            control={
                              <Checkbox
                                checked={form.ciDestinos.includes(destino.value)}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setForm((prev) => ({
                                    ...prev,
                                    ciDestinos: checked
                                      ? [...prev.ciDestinos, destino.value]
                                      : prev.ciDestinos.filter((d) => d !== destino.value),
                                  }));
                                  setWizardErrors((err) => ({ ...err, ciDestinos: undefined }));
                                }}
                              />
                            }
                            label={destino.label}
                          />
                        ))}
                      </FormGroup>
                      <FormHelperText>
                        {wizardErrors.ciDestinos || 'Define quais setores recebem a atividade da CI.'}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                )}
                {!isFluxoContratacao && (
                <Grid item xs={12}>
                  <FormControl
                    fullWidth
                    size="small"
                    required
                    error={!!wizardErrors.destinatarioIds}
                    disabled={loadingUsuarios && usuarios.length === 0}
                  >
                    <InputLabel>Direcionado para</InputLabel>
                    <Select
                      multiple
                      value={form.destinatarioIds}
                      label="Direcionado para"
                      onChange={(e) => {
                        handleDestinatariosChange(e);
                        setWizardErrors((err) => ({ ...err, destinatarioIds: undefined }));
                      }}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(selected || []).map((id) => {
                            const u = usuarios.find((x) => String(x.id) === String(id));
                            return (
                              <Chip
                                key={id}
                                size="small"
                                label={u ? u.nome : id}
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {usuarios.map((usuario) => (
                        <MenuItem key={usuario.id} value={usuario.id}>
                          <Checkbox checked={form.destinatarioIds.some((id) => String(id) === String(usuario.id))} />
                          <ListItemText
                            primary={usuario.nome}
                            secondary={usuario.email || undefined}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                    {wizardErrors.destinatarioIds ? (
                      <FormHelperText>{wizardErrors.destinatarioIds}</FormHelperText>
                    ) : loadingUsuarios ? (
                      <FormHelperText>Carregando usuários da unidade...</FormHelperText>
                    ) : usuarios.length === 0 ? (
                      <FormHelperText>Nenhum usuário disponível para esta unidade.</FormHelperText>
                    ) : (
                      <FormHelperText>Selecione um ou mais usuários responsáveis.</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                )}
              </Grid>
            </Paper>
            )}

            {wizardStep === 1 && isFluxoContratacao && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: '1px solid rgba(46,125,50,0.14)',
                backgroundColor: 'rgba(255,255,255,0.9)'
              }}
            >
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#2e7d32' }}>
                CI — preenchimento do fiscal/solicitante
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                Preencha o comunicado interno. Em seguida você preenche o TR (não o Setor de Contratos).
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label="Assunto / Título"
                  fullWidth
                  size="small"
                  required
                  value={form.titulo}
                  onChange={(event) => {
                    setForm((prev) => ({ ...prev, titulo: event.target.value }));
                    setWizardErrors((err) => ({ ...err, titulo: undefined }));
                  }}
                  error={!!wizardErrors.titulo}
                  helperText={wizardErrors.titulo || undefined}
                />
                <TextField
                  label="Data de referência"
                  type="date"
                  fullWidth
                  size="small"
                  value={form.dataReferencia}
                  onChange={(event) => setForm((prev) => ({ ...prev, dataReferencia: event.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
                <Box
                  sx={{
                    '& .ql-editor': { minHeight: 180 },
                    '& .ql-container': { border: '1px solid rgba(0,0,0,0.23)' },
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    Corpo da CI *
                  </Typography>
                  <ReactQuill
                    theme="snow"
                    value={form.ciCorpoHtml}
                    onChange={(v) => {
                      setForm((prev) => ({ ...prev, ciCorpoHtml: v }));
                      setWizardErrors((err) => ({ ...err, ciCorpoHtml: undefined }));
                    }}
                    modules={QUILL_WIZARD_MODULES}
                    placeholder="Digite o conteúdo do comunicado interno..."
                  />
                  {wizardErrors.ciCorpoHtml && (
                    <FormHelperText error>{wizardErrors.ciCorpoHtml}</FormHelperText>
                  )}
                </Box>
              </Stack>
            </Paper>
            )}

            {wizardStep === 1 && !isFluxoContratacao && (
            <>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: '1px solid rgba(46,125,50,0.14)',
                backgroundColor: 'rgba(255,255,255,0.9)'
              }}
            >
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#2e7d32' }}>
                Formulário
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Preencha título, vínculos, datas e descrição da solicitação.
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1.5 }}>
                <Grid item xs={12}>
                  <TextField
                    label="Título"
                    fullWidth
                    value={form.titulo}
                    onChange={(event) => {
                      setForm((prev) => ({ ...prev, titulo: event.target.value }));
                      setWizardErrors((err) => ({ ...err, titulo: undefined }));
                    }}
                    size="small"
                    error={!!wizardErrors.titulo}
                    helperText={wizardErrors.titulo || undefined}
                    placeholder="Ex: Solicitação de renovação de contrato"
                  />
                </Grid>
                {form.tipo === 'CI' && form.ciTipo === 'ADITIVO' && (
                  <Grid item xs={12} sm={6}>
                    <FormControl
                      fullWidth
                      size="small"
                      required
                      disabled={loadingContratos && contratos.length === 0}
                    >
                      <InputLabel>Contrato Relacionado</InputLabel>
                      <Select
                        value={form.contratoId}
                        label="Contrato Relacionado"
                        onChange={(event) => setForm((prev) => ({ ...prev, contratoId: event.target.value }))}
                      >
                        <MenuItem value="">
                          <em>Selecione o contrato</em>
                        </MenuItem>
                        {contratos.map((contrato) => {
                          const labelParts = [];
                          if (contrato.numero) labelParts.push(contrato.numero);
                          if (contrato.empresa) labelParts.push(contrato.empresa);
                          return (
                            <MenuItem key={contrato.id} value={contrato.id}>
                              {labelParts.join(' · ')}
                            </MenuItem>
                          );
                        })}
                      </Select>
                      {loadingContratos ? (
                        <FormHelperText>Carregando contratos...</FormHelperText>
                      ) : contratos.length === 0 ? (
                        <FormHelperText>Nenhum contrato encontrado para esta unidade.</FormHelperText>
                      ) : (
                        <FormHelperText>Selecione o contrato que receberá o aditivo.</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={form.tipo === 'NOTIFICACAO' ? 'Data da ocorrência' : 'Data de Referência'}
                    type="date"
                    fullWidth
                    size="small"
                    value={form.dataReferencia}
                    onChange={(event) => setForm((prev) => ({ ...prev, dataReferencia: event.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    helperText={
                      form.tipo === 'NOTIFICACAO'
                        ? 'Mesma data usada depois em “Gerar Notificação”.'
                        : undefined
                    }
                  />
                </Grid>
                {form.tipo === 'NOTIFICACAO' && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Prazo para resposta"
                      type="date"
                      fullWidth
                      size="small"
                      value={form.dataPrazo}
                      onChange={(event) => setForm((prev) => ({ ...prev, dataPrazo: event.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      helperText="Herdado automaticamente ao abrir “Gerar Notificação”. Lembrete 1 dia antes."
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    label="Descrição / Detalhes"
                    fullWidth
                    multiline
                    minRows={4}
                    value={form.descricao}
                    onChange={(event) => setForm((prev) => ({ ...prev, descricao: event.target.value }))}
                    placeholder="Descreva a solicitação..."
                  />
                </Grid>
              </Grid>
            </Paper>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: '1px dashed rgba(25,118,210,0.35)',
                backgroundColor: 'rgba(227,242,253,0.5)'
              }}
            >
              <Typography variant="subtitle1" fontWeight={700} color="primary">
                Anexos e documentos
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
                {form.tipo === 'CI' && form.ciTipo === 'ADITIVO'
                  ? 'Anexe a CI do aditivo.'
                  : 'Inclua documentos de suporte.'}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
                {(form.tipo === 'CI' || form.tipo === 'NOTIFICACAO') && (
                  <>
                    <Button
                      variant="outlined"
                      startIcon={<UploadFileIcon />}
                      onClick={() => ciFileInputRef.current?.click()}
                      sx={{ textTransform: 'none', borderRadius: 2 }}
                    >
                      {ciFile ? 'Trocar CI / documento' : form.tipo === 'CI' ? 'Anexar CI' : 'Anexar documento'}
                    </Button>
                    <input
                      ref={ciFileInputRef}
                      type="file"
                      accept=".pdf,application/pdf"
                      hidden
                      onChange={handleCiFileSelect}
                    />
                  </>
                )}
              </Stack>
              <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                {ciFile ? (
                  <Typography variant="caption" color="text.secondary">
                    CI / documento: {ciFile.name}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Nenhum arquivo selecionado.
                  </Typography>
                )}
              </Stack>
            </Paper>
            </>
            )}

            {isFluxoContratacao && wizardStep === 2 && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: '1px solid rgba(124,58,237,0.25)',
                backgroundColor: 'rgba(255,255,255,0.9)'
              }}
            >
              <Typography variant="subtitle1" fontWeight={700} color="secondary">
                TR — preenchimento do fiscal/solicitante
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                Preencha os campos do formulário (configurados no fluxo). Opcionalmente abra o editor
                completo para cláusulas e moldura.
              </Typography>
              {wizardDraftCodigo && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Rascunho: {wizardDraftCodigo}
                </Typography>
              )}
              <Alert severity={trWizardOk ? 'success' : 'warning'} sx={{ mb: 2 }}>
                {trWizardOk
                  ? 'TR salvo pelo solicitante. Pode avançar para autorizadores e abrir o processo.'
                  : 'Preencha ao menos o campo Objeto (ou salve as cláusulas no editor) antes de avançar.'}
              </Alert>
              {wizardErrors.tr && (
                <FormHelperText error sx={{ mb: 1 }}>{wizardErrors.tr}</FormHelperText>
              )}

              {wizardDraftId ? (
                <Box sx={{ mb: 2 }}>
                  <CamposTrDinamicosForm
                    campos={camposTrSchema}
                    valores={camposTrValores}
                    onChange={setCamposTrValores}
                  />
                  <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                    <Button
                      variant="contained"
                      color="secondary"
                      disabled={salvandoCamposTr}
                      onClick={salvarCamposTrWizard}
                      startIcon={salvandoCamposTr ? <CircularProgress size={16} color="inherit" /> : null}
                      sx={{ textTransform: 'none' }}
                    >
                      Salvar campos do TR
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={<DescriptionIcon />}
                      onClick={abrirTrNoWizard}
                      sx={{ textTransform: 'none' }}
                    >
                      {trWizardOk ? 'Editor completo (cláusulas)' : 'Abrir editor de cláusulas'}
                    </Button>
                  </Stack>
                </Box>
              ) : (
                <Alert severity="info">Salve a CI no passo anterior para liberar o TR.</Alert>
              )}
            </Paper>
            )}

            {isFluxoContratacao && wizardStep === 3 && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: '1px solid rgba(13,148,136,0.25)',
                backgroundColor: 'rgba(255,255,255,0.9)'
              }}
            >
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#0f766e' }}>
                Autorizadores / assinantes do TR
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                Cada selecionado recebe atividade e participa da Assinatura Digital do TR.
              </Typography>
              <FormControl fullWidth size="small" error={!!wizardErrors.autorizadorIds}>
                <InputLabel>Autorizadores</InputLabel>
                <Select
                  multiple
                  label="Autorizadores"
                  value={form.autorizadorIds}
                  onChange={(e) => {
                    const value = e.target.value;
                    const valores = typeof value === 'string' ? value.split(',') : value;
                    setForm((prev) => ({
                      ...prev,
                      autorizadorIds: valores
                        .map((id) => (typeof id === 'number' ? id : Number(id)))
                        .filter((id) => Number.isFinite(id)),
                    }));
                    setWizardErrors((err) => ({ ...err, autorizadorIds: undefined }));
                  }}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected || []).map((id) => {
                        const u = usuarios.find((x) => String(x.id) === String(id));
                        return <Chip key={id} size="small" label={u?.nome || id} />;
                      })}
                    </Box>
                  )}
                >
                  {usuarios.map((usuario) => (
                    <MenuItem key={usuario.id} value={usuario.id}>
                      <Checkbox checked={form.autorizadorIds.some((id) => String(id) === String(usuario.id))} />
                      <ListItemText primary={usuario.nome} secondary={usuario.email || undefined} />
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {wizardErrors.autorizadorIds ||
                    (loadingUsuarios ? 'Carregando usuários...' : 'Obrigatório para enviar ao processo.')}
                </FormHelperText>
              </FormControl>
            </Paper>
            )}

            {wizardStep === WIZARD_STEPS.length - 1 && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: '1px solid rgba(25,118,210,0.15)',
                backgroundColor: 'rgba(255,255,255,0.95)'
              }}
            >
              <Typography variant="subtitle1" fontWeight={700} color="primary" sx={{ mb: 1.5 }}>
                Revisão e envio
              </Typography>
              <Stack spacing={1}>
                <Typography variant="body2">
                  <strong>Tipo:</strong> {tipoSolicitacaoLabel(form.tipo)}
                  {form.ciTipo ? ` · ${form.ciTipo === 'CONTRATACAO' ? 'Contratação' : 'Aditivo'}` : ''}
                  {form.subtipo ? ` · ${form.subtipo}` : ''}
                </Typography>
                <Typography variant="body2">
                  <strong>Título:</strong> {form.titulo || '—'}
                </Typography>
                {wizardDraftCodigo && (
                  <Typography variant="body2">
                    <strong>Rascunho:</strong> {wizardDraftCodigo}
                  </Typography>
                )}
                {isFluxoContratacao && (
                  <>
                    <Typography variant="body2">
                      <strong>CI:</strong> {corpoCiTemConteudo(form.ciCorpoHtml) ? 'Preenchida no sistema' : '—'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>TR:</strong> {trWizardOk ? 'Salvo' : 'Pendente'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Autorizadores:</strong>{' '}
                      {(form.autorizadorIds || [])
                        .map((id) => usuarios.find((u) => String(u.id) === String(id))?.nome || id)
                        .join(', ') || '—'}
                    </Typography>
                  </>
                )}
                {!isFluxoContratacao && form.destinatarioIds?.length > 0 && (
                  <Typography variant="body2">
                    <strong>Direcionado para:</strong>{' '}
                    {form.destinatarioIds
                      .map((id) => usuarios.find((u) => String(u.id) === String(id))?.nome || id)
                      .join(', ')}
                  </Typography>
                )}
              </Stack>
            </Paper>
            )}

          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          {wizardStep > 0 && (
            <Button onClick={voltarWizard} disabled={saving} sx={{ textTransform: 'none' }}>
              Voltar
            </Button>
          )}
          {wizardStep < WIZARD_STEPS.length - 1 ? (
            <Button
              variant="contained"
              onClick={avancarWizard}
              disabled={saving || criandoRascunho}
              startIcon={criandoRascunho ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ textTransform: 'none' }}
            >
              {criandoRascunho ? 'Salvando CI...' : 'Próximo'}
            </Button>
          ) : (
          <Button
            onClick={handleSubmit}
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            disabled={saving}
            sx={{ textTransform: 'none' }}
          >
            {saving
              ? 'Enviando...'
              : isFluxoContratacao
                ? 'Abrir processo de contratação'
                : 'Enviar solicitação'}
          </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={detalhesSolicitacao.open} onClose={fecharDetalhesSolicitacao} maxWidth="md" fullWidth>
        <DialogTitle>Detalhes da solicitação</DialogTitle>
        <DialogContent dividers>
          {solicitacaoSelecionada ? (
            <Stack spacing={2.5} sx={{ mt: 0.5 }}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Código
                </Typography>
                <Typography variant="h6" fontWeight={800} color="primary">
                  {solicitacaoSelecionada.codigo}
                </Typography>
              </Box>

              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status atual
                  </Typography>
                  <Chip
                    size="small"
                    label={STATUS_LABELS[solicitacaoSelecionada.status] || solicitacaoSelecionada.status || 'Rascunho'}
                    color={
                      (solicitacaoSelecionada.status || '').toUpperCase() === 'EM_FLUXO'
                        ? 'primary'
                        : ['APROVADA', 'CONCLUIDA'].includes((solicitacaoSelecionada.status || '').toUpperCase())
                        ? 'success'
                        : 'default'
                    }
                  />
                  {solicitacaoSelecionada.processo?.codigo && (
                    <Chip size="small" variant="outlined" label={`Processo ${solicitacaoSelecionada.processo.codigo}`} />
                  )}
                </Box>
                <Stepper activeStep={calcularEtapaFluxo(solicitacaoSelecionada)} alternativeLabel sx={{ mb: 1 }}>
                  {getFluxoEtapas(solicitacaoSelecionada).map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Tipo da solicitação
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {solicitacaoSelecionada.tipo === 'CI'
                      ? 'CI (Comunicado Interno)'
                      : solicitacaoSelecionada.tipo === 'TR'
                      ? 'Termo de Referência'
                      : 'Notificação'}
                  </Typography>
                  {solicitacaoSelecionada.tipo === 'CI' && solicitacaoSelecionada.ciTipo && (
                    <Typography variant="body2" color="text.secondary">
                      Finalidade: {CI_TIPO_LABEL[solicitacaoSelecionada.ciTipo] || solicitacaoSelecionada.ciTipo}
                    </Typography>
                  )}
                  {solicitacaoSelecionada.subtipo && (
                    <Typography variant="body2" color="text.secondary">
                      Subtipo: {solicitacaoSelecionada.subtipo}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Criado em
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {solicitacaoSelecionada.createdAt ? formatDateTime(solicitacaoSelecionada.createdAt) : '—'}
                  </Typography>
                  {solicitacaoSelecionada.dataReferencia && (
                    <Typography variant="body2" color="text.secondary">
                      {solicitacaoSelecionada.tipo === 'NOTIFICACAO' ? 'Data da ocorrência' : 'Data de referência'}
                      : {formatDate(solicitacaoSelecionada.dataReferencia)}
                    </Typography>
                  )}
                  {solicitacaoSelecionada.dataPrazo && (
                    <Typography variant="body2" color="text.secondary">
                      {solicitacaoSelecionada.tipo === 'NOTIFICACAO' ? 'Prazo para resposta' : 'Data de prazo (devolução)'}
                      : {formatDate(solicitacaoSelecionada.dataPrazo)}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Destinatário(s)
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                    {nomesDestinatarios(solicitacaoSelecionada).length ? (
                      nomesDestinatarios(solicitacaoSelecionada).map((nome) => (
                        <Chip key={nome} size="small" label={nome} />
                      ))
                    ) : (
                      <Typography variant="body1" fontWeight={600}>
                        —
                      </Typography>
                    )}
                  </Stack>
                  {solicitacaoSelecionada.destinatario?.email && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {solicitacaoSelecionada.destinatario.email}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Contrato vinculado
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {solicitacaoSelecionada.contrato
                      ? `${solicitacaoSelecionada.contrato.numero || ''} ${solicitacaoSelecionada.contrato.empresa || ''}`.trim()
                      : '—'}
                  </Typography>
                </Grid>
              </Grid>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Título
                </Typography>
                <Typography variant="body1" fontWeight={700}>
                  {solicitacaoSelecionada.titulo}
                </Typography>
              </Box>

              {solicitacaoSelecionada.descricao && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Descrição
                  </Typography>
                  <Typography variant="body2" color="text.primary">
                    {solicitacaoSelecionada.descricao}
                  </Typography>
                </Box>
              )}

              {detalhesDestinos.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Destinos
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {detalhesDestinos.map((destino) => (
                      <Chip key={destino} label={destino} color="primary" variant="outlined" />
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={160}>
              <CircularProgress size={28} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {solicitacaoSelecionada?.arquivoUrl && (
            <Button
              onClick={() => openAuthenticatedUpload(solicitacaoSelecionada.arquivoUrl)}
              startIcon={<AttachmentIcon fontSize="small" />}
              sx={{ textTransform: 'none' }}
            >
              Abrir anexo
            </Button>
          )}
          <Button onClick={fecharDetalhesSolicitacao} sx={{ textTransform: 'none' }}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={excluirSolicitacao.open}
        onClose={excluirSolicitacao.loading ? undefined : cancelarExclusaoSolicitacao}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remover solicitação</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} alignItems="center" textAlign="center">
            <WarningAmberIcon color="warning" sx={{ fontSize: 42 }} />
            <Typography variant="body1" fontWeight={600}>
              Confirma a remoção da solicitação {excluirSolicitacao.dados?.codigo || ''}?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Esta ação apagará também as atividades e processos associados.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={cancelarExclusaoSolicitacao}
            disabled={excluirSolicitacao.loading}
            sx={{ textTransform: 'none' }}
          >
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            startIcon={<DeleteOutlineIcon fontSize="small" />}
            disabled={excluirSolicitacao.loading}
            onClick={confirmarExclusaoSolicitacao}
            sx={{ textTransform: 'none' }}
          >
            {excluirSolicitacao.loading ? <CircularProgress size={18} color="inherit" /> : 'Excluir' }
          </Button>
        </DialogActions>
      </Dialog>

      <EditorTRModal
        open={dialogEditarTROpen}
        onClose={fecharEditarTR}
        solicitacaoId={solicitacaoTRCtx.id}
        assuntoCI={solicitacaoTRCtx.titulo}
      />

      <EditorCISolicitacaoModal
        open={dialogEditarCIOpen}
        onClose={fecharEditarCI}
        solicitacaoId={solicitacaoCICtx.id}
        tituloInicial={solicitacaoCICtx.titulo}
      />

      <AutorizadoresTrDialog
        open={dialogAutorizadores.open}
        onClose={() => setDialogAutorizadores({ open: false, item: null })}
        solicitacao={dialogAutorizadores.item}
        usuarios={usuarios}
        loadingUsuarios={loadingUsuarios}
        onConfirm={confirmarEnvioComAutorizadores}
      />

      <SolicitacaoAditivoDialog
        open={drawerAditivo.open}
        item={drawerAditivo.item}
        onClose={() => setDrawerAditivo({ open: false, item: null })}
        onSaved={() => {
          setSnackbar({ open: true, message: 'Aditivo salvo com sucesso.', severity: 'success' });
          carregarSolicitacoes();
        }}
        onError={(message) =>
          setSnackbar({ open: true, message: message || 'Erro no aditivo', severity: 'error' })
        }
      />

      <EditorNotificacaoSolicitacaoDialog
        open={drawerNotificacao.open}
        solicitacao={drawerNotificacao.item}
        onClose={() => setDrawerNotificacao({ open: false, item: null })}
        onSaved={carregarSolicitacoes}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}


